import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import './UsernamePrompt.css';

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

interface Props {
  onDone: () => void;
}

export default function UsernamePrompt({ onDone }: Props) {
  const { t } = useTranslation();
  const { token, updateCustomer } = useCustomerAuth();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (raw: string) => {
    const clean = raw.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
    setValue(clean);
    setError('');

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (clean.length < 3) return;

    debounceRef.current = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await axios.get(`${API_URL || ''}/api/friends/check-username/${clean}`);
        if (!data.available) {
          setError(data.reason === 'invalid'
            ? t('username.invalid', 'Invalid format')
            : t('username.taken', 'Already taken'));
        }
      } catch { /* ignore */ }
      setChecking(false);
    }, 400);
  };

  const handleSubmit = async () => {
    if (!USERNAME_RE.test(value) || !token) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_URL || ''}/api/friends/set-username`,
        { username: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateCustomer({ username: value });
      onDone();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.error;
      setError(msg || t('username.error', 'Could not save'));
    }
    setSaving(false);
  };

  const isValid = USERNAME_RE.test(value) && !error && !checking;

  return (
    <div className="up-overlay">
      <div className="up-card">
        <div className="up-title">{t('username.title', 'Choose your username')}</div>
        <div className="up-subtitle">
          {t('username.subtitle', 'Friends can find you by username. Latin letters, numbers, and underscores only.')}
        </div>

        <div className="up-input-wrap">
          <span className="up-at">@</span>
          <input
            ref={inputRef}
            className="up-input"
            value={value}
            onChange={e => handleChange(e.target.value)}
            placeholder="cooluser"
            maxLength={20}
            autoCapitalize="off"
            autoCorrect="off"
            onKeyDown={e => { if (e.key === 'Enter' && isValid) handleSubmit(); }}
          />
          {checking && <span className="up-spinner" />}
        </div>

        {error && <div className="up-error">{error}</div>}
        {value.length > 0 && value.length < 3 && !error && (
          <div className="up-hint">{t('username.minLength', 'Minimum 3 characters')}</div>
        )}

        <button
          className="up-btn"
          disabled={!isValid || saving}
          onClick={handleSubmit}
        >
          {saving ? '...' : t('username.save', 'Continue')}
        </button>

        <button className="up-skip" onClick={onDone}>
          {t('username.skip', 'Skip for now')}
        </button>
      </div>
    </div>
  );
}
